import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const WatermarkContainer = styled.div`
  position: absolute;
  transition: ${props => props.smooth ? 'all 2s ease-in-out' : 'none'};
  z-index: 5;
  pointer-events: none;
  opacity: ${props => props.opacity};
`;

const WatermarkImage = styled.img`
  max-width: ${props =>
    props.size === 'small'  ? '60px'  :
    props.size === 'medium' ? '100px' :
    props.size === 'large'  ? '140px' : '160px'
  };
  max-height: ${props =>
    props.size === 'small'  ? '60px'  :
    props.size === 'medium' ? '100px' :
    props.size === 'large'  ? '140px' : '160px'
  };
  object-fit: contain;
`;

const WatermarkText = styled.div`
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.5);
  color: ${props => props.textColor || 'white'};
  border-radius: 4px;
  font-family: ${props => props.fontFamily || 'Arial'};
  font-size: ${props => props.fontSize ? `${props.fontSize}px` :
    props.size === 'small'  ? '12px' :
    props.size === 'medium' ? '14px' : '16px'
  };
  font-weight: 600;
  white-space: nowrap;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
`;

// ─── Resolve active position mode ────────────────────────────────────────────
// Supports new unified `positionMode` field and old separate flags for
// backwards compatibility.
const resolvePositionMode = (settings) => {
  if (settings.positionMode) return settings.positionMode;
  if (settings.dynamicMovement) return 'dynamic';
  if (settings.customPosition?.enabled) return 'custom';
  return 'preset';
};

// ─── CSS style for preset / custom modes (no JS pixel math needed) ───────────
const getStaticStyle = (settings) => {
  const mode = resolvePositionMode(settings);

  if (mode === 'custom') {
    const x = settings.customPosition?.x ?? 85;
    const y = settings.customPosition?.y ?? 85;
    return { left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' };
  }

  // preset
  const M = '12px';
  const presetMap = {
    'top-left':      { top: M,     left: M,     transform: 'none' },
    'top-center':    { top: M,     left: '50%', transform: 'translateX(-50%)' },
    'top-right':     { top: M,     right: M,    transform: 'none' },
    'center-left':   { top: '50%', left: M,     transform: 'translateY(-50%)' },
    'center-center': { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
    'center-right':  { top: '50%', right: M,    transform: 'translateY(-50%)' },
    'bottom-left':   { bottom: M,  left: M,     transform: 'none' },
    'bottom-center': { bottom: M,  left: '50%', transform: 'translateX(-50%)' },
    'bottom-right':  { bottom: M,  right: M,    transform: 'none' },
  };
  return presetMap[settings.position] || presetMap['bottom-right'];
};

const DynamicWatermark = ({
  settings,
  currentTime,
  videoDimensions = { width: 640, height: 360 }
}) => {
  const [dynPosition, setDynPosition] = useState(null);
  const [isSmooth, setIsSmooth]       = useState(true);

  // ── helpers for dynamic movement (pixel-based) ──────────────────────────
  const getWatermarkDimensions = () => {
    if (settings.type === 'text') {
      const fontSize   = settings.fontSize || 14;
      const textLength = (settings.text || 'LOGO').length;
      return { width: Math.max(textLength * fontSize * 0.6 + 24, 60), height: fontSize + 16 };
    }
    const sizeMap = { small: 60, medium: 100, large: 140, xlarge: 160 };
    const s = sizeMap[settings.size] || 100;
    return { width: s, height: s };
  };

  const getMovementSpeed = () => ({ slow: 5, fast: 1 }[settings.movementSpeed] ?? 3);

  const calculateDynPosition = (time, pattern) => {
    const margin = 20;
    const wDims  = getWatermarkDimensions();
    const maxX   = Math.max(0, videoDimensions.width  - wDims.width  - margin);
    const maxY   = Math.max(0, videoDimensions.height - wDims.height - margin);
    const clamp  = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

    switch (pattern) {
      case 'circular': {
        const angle = (time / 10) * 2 * Math.PI;
        const cx = maxX / 2, cy = maxY / 2;
        const r  = Math.min(cx, cy) * 0.6;
        return { x: clamp(cx + Math.cos(angle) * r, margin, maxX), y: clamp(cy + Math.sin(angle) * r, margin, maxY) };
      }
      case 'horizontal': {
        const p = (time / 8) % 1;
        const xPos = p < 0.5 ? p * 2 * maxX : (1 - p) * 2 * maxX;
        return { x: margin + clamp(xPos, 0, maxX - margin), y: margin + maxY / 2 };
      }
      case 'vertical': {
        const p = (time / 6) % 1;
        const yPos = p < 0.5 ? p * 2 * maxY : (1 - p) * 2 * maxY;
        return { x: margin + maxX / 2, y: margin + clamp(yPos, 0, maxY - margin) };
      }
      case 'corners': {
        const corners = [{ x: margin, y: margin }, { x: maxX, y: margin }, { x: maxX, y: maxY }, { x: margin, y: maxY }];
        return corners[Math.floor(time / 4) % 4];
      }
      case 'random':
      default: {
        const seed = Math.floor(time / getMovementSpeed());
        return {
          x: margin + Math.abs((Math.sin(seed * 12.9898) * 43758.5453) % 1) * maxX,
          y: margin + Math.abs((Math.sin(seed * 78.233)  * 43758.5453) % 1) * maxY,
        };
      }
    }
  };

  useEffect(() => {
    if (!settings?.enabled) return;
    if (resolvePositionMode(settings) === 'dynamic') {
      setDynPosition(calculateDynPosition(currentTime, settings.movementPattern));
      setIsSmooth(true);
    } else {
      setDynPosition(null);
      setIsSmooth(false);
    }
  }, [currentTime, settings, videoDimensions]);

  // ── guard ────────────────────────────────────────────────────────────────
  if (!settings || !settings.enabled) return null;

  const mode           = resolvePositionMode(settings);
  const containerStyle = (mode === 'dynamic' && dynPosition)
    ? { left: `${Math.max(0, dynPosition.x)}px`, top: `${Math.max(0, dynPosition.y)}px` }
    : getStaticStyle(settings);

  return (
    <WatermarkContainer
      style={containerStyle}
      opacity={settings.opacity ?? 0.8}
      smooth={isSmooth && mode === 'dynamic'}
    >
      {settings.type === 'image' && (settings.path || settings._previewDataUrl) ? (
        <WatermarkImage 
          src={
            settings._previewDataUrl || 
            (settings.path?.startsWith('data:') || settings.path?.startsWith('http') 
              ? settings.path 
              : `http://localhost:8001/get-watermark?path=${encodeURIComponent(settings.path)}`)
          } 
          alt="Watermark" 
          size={settings.size} 
        />
      ) : (
        <WatermarkText
          size={settings.size}
          textColor={settings.textColor}
          fontFamily={settings.font}
          fontSize={settings.fontSize}
        >
          {settings.text || 'LOGO'}
        </WatermarkText>
      )}
    </WatermarkContainer>
  );
};

export default DynamicWatermark;